/* eslint-disable react-hooks/exhaustive-deps */
import {
  FormControl,
  Stack,
  TextField,
  IconButton,
  useTheme,
  Button,
  useMediaQuery,
  Typography,
  Chip,
  styled,
  Tooltip,
  Box
} from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { useLocation, useNavigate } from 'react-router';
import { KeyboardEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  initialState,
  reducerUpdateAdvanceClear,
  reducerUpdateAdvanceSearch,
  reducerUpdateAdvanceSearchIncludeKeywords
} from 'src/features/advanceSearchSocialMedia';
import AdvanceSearchSocialMedia from 'src/components/AdvanceSearchSocialMedia';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import changeObjectToQuery from 'src/utils/changeObjectToQuery';
import TooltipCustomize from 'src/components/TooltipCustomize';
import RefreshIcon from '@mui/icons-material/Refresh';
import SelectAutoRefreshInterval from 'src/components/SelectAutoRefreshInterval';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import useRefreshObjectList from 'src/hooks/useRefreshObjectList';
import DateRangeLabel from 'src/layouts/SidebarLayout/components/DateRangeLabel';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';
import { RestoreFromTrash } from '@mui/icons-material';
import { colors } from 'src/theme/schemes/PureLightTheme';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { CUSTOM_REPORT } from 'src/route';

const ChipCustom = styled(Chip)(
  ({ theme }) => `
    background-color: ${theme.palette.primary.light};
    border-radius: ${theme.spacing(1)};
    padding: 10px ${theme.spacing(2)};
    height: 29px;
    width: 100px;
    .MuiChip-label {
        color: #0A789D;
        font-size: 14px;
        line-height: 130%;
        margin: 0 4px;
    }
    .MuiChip-deleteIcon {
        font-size: 14px;
        color: #0A789D;
    }
  `
);

function BottomHeader() {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const title = pathname.split('/')[2];

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const advanceSearch = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );

  const {
    streamsCommentingToPost,
    selectedOriginalToPost,
    searchOriginalToPost,
    ...rest
  } = advanceSearch;
  const query = changeObjectToQuery(rest);
  const [localSearch, setLocalSearch] = useState<string>(query);

  const advanceSearchKeys = query
    ? query.split(';').slice(0, query.split(';').length - 1)
    : [];

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setLocalSearch('');
    dispatch(reducerUpdateAdvanceClear(initialState));
  };

  const handleSearch = () => {
    dispatch(reducerUpdateAdvanceSearchIncludeKeywords([localSearch]));
  };

  const handleDeleteFilter = (item: string) => {
    const splitFilter = item.split(':');
    const deletedFilter = splitFilter[0].trim();
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearch,
        [deletedFilter]: initialState[deletedFilter]
      })
    );
  };

  const [open, setOpen] = useState<boolean>(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const refreshObjectList = useRefreshObjectList();

  const handleRefresh = () => {
    refreshObjectList();
  };

  useEffect(() => {
    setLocalSearch(query);
  }, [advanceSearch]);

  const handleDeleteAllFilter = () => {
    dispatch(reducerUpdateAdvanceSearch(initialState));
  };

  // auto refresh metric
  const { selectedAutoRefreshInterval } = useAppSelector(
    (store) => store.autoRefreshMetric
  );
  useEffect(() => {
    if (selectedAutoRefreshInterval === 0) return;
    const interval = setInterval(() => {
      handleRefresh();
    }, selectedAutoRefreshInterval);

    return () => clearInterval(interval);
  }, [selectedAutoRefreshInterval]);

  const {
    isOnTour,
    currentTour: { stepNumber, menu, submenu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourStep12 =
    isOnTour &&
    stepNumber === 12 &&
    menu === 'social-media' &&
    submenu === 'overview';

  const isCurrentlyOnTourStep14 =
    isOnTour &&
    stepNumber === 14 &&
    menu === 'social-media' &&
    submenu === 'overview';

  return (
    <Stack direction="column" my={2}>
      <Stack
        direction={isLargeScreen ? 'row' : 'column'}
        justifyContent={isLargeScreen ? 'start' : 'unset'}
        gap={1}
        alignItems="center"
      >
        <ProductTourTooltip
          open={isCurrentlyOnTourStep12}
          placement="bottom"
          onClick={handleClickOpen}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            className="filtering"
            width="100%"
            maxWidth={isLargeScreen ? '400px' : 'unset'}
            sx={
              isCurrentlyOnTourStep12
                ? {
                    position: 'relative',
                    zIndex: 1000,
                    backgroundColor: 'white'
                  }
                : {}
            }
          >
            <TextField
              size="small"
              InputProps={{
                startAdornment: (
                  <IconButton
                    size="small"
                    onClick={!isOnTour ? handleSearch : undefined}
                  >
                    <SearchTwoToneIcon htmlColor="#757575" />
                  </IconButton>
                ),
                sx: { borderRadius: '16px', backgroundColor: 'white' },
                endAdornment: localSearch ? (
                  <IconButton
                    size="small"
                    onClick={!isOnTour ? handleClear : undefined}
                  >
                    <ClearIcon />
                  </IconButton>
                ) : null
              }}
              placeholder="Search stream"
              onChange={
                !isOnTour ? (e) => setLocalSearch(e.target.value) : undefined
              }
              onKeyDown={!isOnTour ? handleKeyDown : undefined}
              fullWidth
            />
            <SelectDateRangeRetail
              selectProps={{
                sx: {
                  borderRadius: '16px',
                  width: '200px'
                }
              }}
            />
          </Stack>
        </ProductTourTooltip>
        <Stack
          direction="row"
          justifyContent={isLargeScreen ? 'unset' : 'space-between'}
          spacing={1}
          className="timeframeAndRefresh"
          alignItems="center"
        >
          <ProductTourTooltip open={isCurrentlyOnTourStep14}>
            <FormControl
              size="small"
              sx={{
                ...(isCurrentlyOnTourStep14
                  ? {
                      position: 'relative',
                      zIndex: 1000
                    }
                  : {})
              }}
            >
              <TooltipCustomize title="Advance search" placement="top-start">
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: '16px',
                    height: theme.spacing(5),
                    minWidth: theme.spacing(6),
                    background: theme.palette.primary.main
                  }}
                  onClick={!isOnTour ? () => handleClickOpen() : undefined}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FilterListIcon htmlColor="#FFFFFF" />
                    <Typography color="white" fontWeight={600}>
                      Filter
                    </Typography>
                  </Stack>
                </Button>
              </TooltipCustomize>
              {open && (
                <AdvanceSearchSocialMedia open={open} onClose={handleClose} />
              )}
            </FormControl>
          </ProductTourTooltip>
          <Stack
            direction="row"
            overflow="auto"
            maxWidth={300}
            spacing={1}
            sx={{
              scrollbarWidth: 'thin'
            }}
          >
            {advanceSearchKeys
              ? advanceSearchKeys.map((item) => {
                  return (
                    <Tooltip title={item}>
                      <ChipCustom
                        variant="filled"
                        deleteIcon={<ClearIcon />}
                        onDelete={() => handleDeleteFilter(item)}
                        label={item}
                        size="small"
                      />
                    </Tooltip>
                  );
                })
              : null}
          </Stack>
          {advanceSearchKeys.length > 0 ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={handleDeleteAllFilter}
            >
              <RestoreFromTrash fontSize="small" />
              <Typography fontSize="14px">Clear Filters</Typography>
            </Stack>
          ) : null}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" gap={1} sx={{ ml: isLargeScreen ? 'auto' : 0 }}>
          {title !== 'clustering' && (
            <TooltipCustomize title="Refresh all metric" placement="top-start">
              <Button
                variant="contained"
                onClick={handleRefresh}
                sx={{
                  borderRadius: '6px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6),
                  background: theme.palette.primary.main
                }}
              >
                <RefreshIcon htmlColor={theme.colors.primary.lighter} />
              </Button>
            </TooltipCustomize>
          )}
          <FormControl size="small" sx={{ width: 100 }}>
            <SelectAutoRefreshInterval />
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ background: colors.gradients.blue1, fontWeight: 600 }}
            onClick={() => {
              navigate(`${CUSTOM_REPORT}`);
            }}
          >
            Create Report
          </Button>
        </Stack>
      </Stack>
      <DateRangeLabel label={`Viewing ${title} within`} />
    </Stack>
  );
}

export default BottomHeader;
